import { Play, Pause, Square } from "lucide-react";
import { useTimerContext } from "@/v2/contexts/TimerProvider";
import { useIsMobile } from "@/v2/hooks/useIsMobile";
import { cn } from "@/v2/lib/utils";

function formatTime(totalSeconds: number): string {
	const h = Math.floor(totalSeconds / 3600);
	const m = Math.floor((totalSeconds % 3600) / 60);
	const s = totalSeconds % 60;
	const mm = String(m).padStart(2, "0");
	const ss = String(s).padStart(2, "0");
	return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

export function TimerPill() {
	const timer = useTimerContext();
	const isMobile = useIsMobile();

	if (timer.phase === "idle") return null;

	const displaySeconds = timer.phase === "stopped" ? timer.finalSeconds : timer.elapsedSeconds;
	const isRunning = timer.phase === "running";
	const isPaused = timer.phase === "paused";
	const isStopped = timer.phase === "stopped";

	return (
		<div
			className={cn(
				"fixed z-40 flex items-center gap-1.5 rounded-2xl border border-border bg-background/95 shadow-lg backdrop-blur-sm",
				"animate-in fade-in zoom-in-95 duration-200",
				isMobile
					? "bottom-[calc(4rem+env(safe-area-inset-bottom)+12px)] left-4"
					: "top-4 right-[calc(1rem+var(--sidebar-width,0px))]",
			)}
		>
			{/* Indicator dot */}
			<div className="pl-3.5 flex items-center">
				<div
					className={cn(
						"size-2.5 rounded-full",
						isRunning && "bg-success animate-pulse",
						(isPaused || isStopped) && "bg-warning",
					)}
				/>
			</div>

			{/* Time display */}
			<span className="tabular-nums text-base font-semibold py-2.5 pl-1">
				{formatTime(displaySeconds)}
			</span>

			{/* Action buttons */}
			<div className="flex items-center pr-1.5">
				{isRunning && (
					<>
						<PillButton onClick={timer.pause} label="Pause">
							<Pause className="size-4" />
						</PillButton>
						<PillButton onClick={timer.stop} label="Stop">
							<Square className="size-3.5 fill-current" />
						</PillButton>
					</>
				)}

				{isPaused && (
					<>
						<PillButton onClick={timer.resume} label="Resume">
							<Play className="size-4 fill-current" />
						</PillButton>
						<PillButton onClick={timer.stop} label="Stop">
							<Square className="size-3.5 fill-current" />
						</PillButton>
					</>
				)}

				{isStopped && (
					<>
						<PillButton onClick={timer.resume} label="Resume">
							<Play className="size-4 fill-current" />
						</PillButton>
						<PillButton onClick={timer.requestAddEntry} label="Add entry">
							<Square className="size-3.5 fill-current" />
						</PillButton>
					</>
				)}
			</div>
		</div>
	);
}

function PillButton({
	onClick,
	label,
	children,
}: {
	onClick: () => void;
	label: string;
	children: React.ReactNode;
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			aria-label={label}
			className="inline-flex items-center justify-center size-9 rounded-xl text-muted-foreground transition-colors hover:text-foreground hover:bg-accent active:bg-accent/80"
		>
			{children}
		</button>
	);
}
